"use client";

import { useMemo, useState } from "react";

import { CommentForm } from "@/components/feed/comment-form";
import { ContentReportDialog } from "@/components/feed/content-report-dialog";
import { CommentsEmptyState } from "@/components/feed/feed-states";
import { Skeleton } from "@/components/ui/skeleton";
import { UserAvatar } from "@/components/ui/user-avatar";
import { usePostComments } from "@/hooks/use-feed";
import { formatRelativeTime, getErrorMessage } from "@/lib/utils";

type CommentListProps = {
  postId: string;
  currentUserId: string | null;
  commentsEnabled: boolean;
};

export function CommentList({
  postId,
  currentUserId,
  commentsEnabled,
}: CommentListProps) {
  const commentsQuery = usePostComments(postId, true);
  const [replyTo, setReplyTo] = useState<string | null>(null);

  const comments = useMemo(() => commentsQuery.data ?? [], [commentsQuery.data]);
  const topLevel = useMemo(
    () => comments.filter((comment) => !comment.parentCommentId),
    [comments],
  );
  const repliesByParent = useMemo(() => {
    const grouped = new Map<string, typeof comments>();

    comments.forEach((comment) => {
      if (!comment.parentCommentId) {
        return;
      }

      const list = grouped.get(comment.parentCommentId) ?? [];
      list.push(comment);
      grouped.set(comment.parentCommentId, list);
    });

    return grouped;
  }, [comments]);

  if (commentsQuery.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }

  if (commentsQuery.isError) {
    return (
      <p className="text-sm text-destructive">
        {getErrorMessage(commentsQuery.error)}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {topLevel.length === 0 ? <CommentsEmptyState /> : null}

      <ul className="space-y-4">
        {topLevel.map((comment) => {
          const replies = repliesByParent.get(comment.id) ?? [];

          return (
            <li key={comment.id}>
              <div className="flex items-start gap-3">
                <UserAvatar
                  name={comment.author.fullName}
                  path={comment.author.avatarPath}
                  className="size-8 text-[10px]"
                />
                <div className="min-w-0 flex-1">
                  <div className="rounded-lg bg-muted/50 px-3 py-2">
                    <p className="text-sm font-medium">
                      {comment.author.fullName}
                    </p>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">
                      {comment.content}
                    </p>
                  </div>
                  <div className="mt-1 flex items-center gap-3 px-1 text-xs text-muted-foreground">
                    <time dateTime={comment.createdAt}>
                      {formatRelativeTime(comment.createdAt)}
                    </time>
                    {commentsEnabled ? (
                      <button
                        type="button"
                        className="font-medium hover:text-foreground"
                        onClick={() =>
                          setReplyTo((current) =>
                            current === comment.id ? null : comment.id,
                          )
                        }
                      >
                        Válasz
                      </button>
                    ) : null}
                    {currentUserId && currentUserId !== comment.author.id ? (
                      <ContentReportDialog
                        targetType="comment"
                        targetId={comment.id}
                        compact
                      />
                    ) : null}
                  </div>

                  {replies.length > 0 ? (
                    <ul className="mt-3 space-y-3 border-l pl-3">
                      {replies.map((reply) => (
                        <li key={reply.id} className="flex items-start gap-3">
                          <UserAvatar
                            name={reply.author.fullName}
                            path={reply.author.avatarPath}
                            className="size-7 text-[10px]"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="rounded-lg bg-muted/40 px-3 py-2">
                              <p className="text-sm font-medium">{reply.author.fullName}</p>
                              <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">
                                {reply.content}
                              </p>
                            </div>
                            <div className="mt-1 flex items-center gap-3 px-1 text-xs text-muted-foreground">
                              <time dateTime={reply.createdAt}>
                                {formatRelativeTime(reply.createdAt)}
                              </time>
                              {currentUserId &&
                              currentUserId !== reply.author.id ? (
                                <ContentReportDialog
                                  targetType="comment"
                                  targetId={reply.id}
                                  compact
                                />
                              ) : null}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : null}

                  {replyTo === comment.id ? (
                    <div className="mt-3">
                      <CommentForm
                        postId={postId}
                        currentUserId={currentUserId}
                        parentCommentId={comment.id}
                        placeholder={`Válasz ${comment.author.fullName} hozzászólására...`}
                        compact
                        onSuccess={() => setReplyTo(null)}
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {commentsEnabled ? (
        <CommentForm postId={postId} currentUserId={currentUserId} />
      ) : (
        <p className="text-sm text-muted-foreground">
          A hozzászólások ennél a bejegyzésnél ki vannak kapcsolva.
        </p>
      )}
    </div>
  );
}
